import type {
  OverviewConditionRow,
  OverviewPeriodConditionRow,
} from '../domain/continuousSafety/overviewSiteConditionSummary';
import { formatNowMeasuredWindowCoverage } from '../domain/continuousSafety/overviewSiteConditionSummary';
import type { SiteConditionCurrentActivity } from '../domain/continuousSafety/siteConditionState';
import type { MeasurementOpenWorkItem } from '../domain/continuousSafety/openWorkAttention';
import { formatSiteDateTime, formatSiteDateTimeLocalInput, formatSiteMonthDay, formatSiteTime, openSitePt15mWindowStart, parseSiteDateTimeLocalInput } from '../domain/siteTime/siteTime';
import { resolveIndexedCameraPlacement } from '../utils/locationDisplay';
import {
  OVERVIEW_V2_EMPTY_CONTROL_DETAIL,
  overviewV2ConditionDisplayName,
  overviewV2ControlDisplayName,
  overviewV2OccurrenceHeadline,
  overviewV2RecurringDenominator,
} from './overviewV2CustomerCopy';

export type OverviewV2Stage = {
  value: string;
  detail: string;
  facts?: string[];
  mutedFacts?: string[];
  status?: string | null;
  statusKind?: 'measurement' | 'decision' | 'status';
  tone?: 'red' | 'amber' | 'green' | 'default';
};

export type OverviewV2SummaryTile = {
  id: 'zones' | 'policies' | 'patterns' | 'attention' | 'controls' | 'health';
  label: string;
  value: string;
  detail: string;
  tone?: 'default' | 'alert' | 'amber';
};

export type {
  OverviewV2CameraHealthFacts,
  OverviewV2CameraHealthRow,
} from './overviewV2CameraHealth';
export {
  buildOverviewV2CameraHealthFacts,
  cameraFreshnessLabel,
  cameraLastFrameLabel,
  cameraOnlineStatusLabel,
  formatOverviewV2CameraFreshness,
  formatOverviewV2CameraHealthRow,
  formatOverviewV2CameraMeasuring,
  overviewV2CameraHealthValue,
  overviewV2HealthFreshnessCopy,
} from './overviewV2CameraHealth';

export type OverviewV2NextActionKind =
  | 'review_episodes'
  | 'sign_safety_scan'
  | 'open_control'
  | 'open_safety_case'
  | 'verify_scan'
  | 'investigate'
  | 'view';

export type OverviewV2AttentionItem = {
  id: string;
  conditionId: string;
  reason: string;
  detail?: string;
  location?: string;
  targetPath: string | null;
  ctaLabel?: string;
  conditionLabel?: string;
  pendingReviewCount?: number | null;
  nextActionKind?: OverviewV2NextActionKind;
  source: 'current_condition' | 'condition_attention' | 'open_work' | 'coverage';
  classification: 'DISTINCT_VALID_WORK' | 'DUPLICATE_SAME_WORK' | 'STALE_WORK';
  affectedPolicies?: string[];
  dedupeKey?: string;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

export function formatOverviewV2Count(value: number): string {
  return value.toLocaleString('en-US');
}

function looksLikeWindow(value: string): boolean {
  return /\d/.test(value) && /[-–]|through|window|pm|am/i.test(value);
}

function looksLikeLatchCode(value: string): boolean {
  return /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(value);
}

function currentOccurrenceHeadline(row: OverviewConditionRow): string | null {
  if (typeof row.measurementValue === 'number') {
    return overviewV2OccurrenceHeadline(row.measurementValue);
  }
  const activity = text(row.supportingActivityLabel);
  if (!activity) return null;
  const match = activity.match(/^([\d,]+)\b/);
  if (!match) return activity;
  const count = Number(match[1].replace(/,/g, ''));
  if (!Number.isFinite(count)) return activity;
  return overviewV2OccurrenceHeadline(count);
}

function currentWindowLabel(row: OverviewConditionRow): string {
  if (looksLikeWindow(text(row.coverageLabel))) return row.coverageLabel;
  if (looksLikeWindow(text(row.ageLabel))) return row.ageLabel;
  return 'Current measured window';
}

export function coverageIncompleteFromRow(row: OverviewConditionRow | null | undefined): boolean {
  if (!row) return false;
  if (text(row.stateChipLabel) && /coverage/i.test(text(row.stateChipLabel))) return true;
  return Boolean(
    row.coverageState
    || row.coverageShortfallReason
    || (typeof row.coveragePct === 'number' && row.coveragePct < 1),
  );
}

function measurementStatusChip(row: OverviewConditionRow): string | null {
  const chip = text(row.stateChipLabel);
  if (chip && /coverage/i.test(chip)) return chip;
  if (coverageIncompleteFromRow(row)) return 'Coverage incomplete';
  if (!chip || /threshold exceeded|needs attention|above normal|current activity/i.test(chip)) return null;
  return chip;
}

function formatSiteClock(value: unknown, timeZone?: string | null): string | null {
  const raw = text(value);
  if (!raw) return null;
  return formatSiteDateTime(raw, timeZone) || raw;
}

function latestActivityFact(row: OverviewConditionRow, queryAsOf?: string | null): string | null {
  const from = text(row.latestReadingAt) || text(row.completedAt);
  const to = text(queryAsOf);
  if (from && to) {
    const start = Date.parse(from);
    const end = Date.parse(to);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      const minutes = Math.max(0, Math.round((end - start) / 60000));
      if (minutes < 1) return 'Latest activity: just now';
      if (minutes < 60) return `Latest activity: ${minutes} min ago`;
      const hours = Math.round(minutes / 60);
      return `Latest activity: ${hours} h ago`;
    }
  }
  const age = text(row.ageLabel);
  if (age && !looksLikeWindow(age) && !/^fresh$/i.test(age) && !/measured through/i.test(age)) {
    return `Latest activity: ${age}`;
  }
  return null;
}

function measuredThroughFact(
  row: OverviewConditionRow,
  timeZone?: string | null,
): string | null {
  const clock = formatSiteClock(row.currentThrough || row.completedAt, timeZone);
  return clock ? `Measured through ${clock}` : null;
}

function bucketResolutionFact(measuredWindow?: string | null): string | null {
  const raw = text(measuredWindow);
  if (!raw) return null;
  if (/15\s*minute/i.test(raw)) return '15-minute intervals';
  return raw;
}

function nowCoverageHoursFact(row: OverviewConditionRow): string | null {
  const windowCopy = formatNowMeasuredWindowCoverage(row.windowUsableHours, row.windowExpectedHours);
  if (windowCopy) return windowCopy;
  return null;
}

function nowCompactFacts(
  row: OverviewConditionRow,
  _context?: { queryAsOf?: string | null; measuredWindow?: string | null; timeZone?: string | null },
): string[] {
  const window = currentWindowLabel(row);
  return [
    window && window !== 'Current measured window' ? window : null,
  ].filter((line): line is string => Boolean(line));
}

function nowMutedFacts(
  row: OverviewConditionRow,
  context?: { queryAsOf?: string | null; measuredWindow?: string | null; timeZone?: string | null },
): string[] {
  return [
    latestActivityFact(row, context?.queryAsOf),
    bucketResolutionFact(context?.measuredWindow),
    nowCoverageHoursFact(row),
  ].filter((line): line is string => Boolean(line));
}

export function currentPolicyStage(
  row: OverviewConditionRow | null,
  context?: { queryAsOf?: string | null; measuredWindow?: string | null; timeZone?: string | null },
): OverviewV2Stage {
  const unavailable: OverviewV2Stage = {
    value: 'Not currently measuring',
    detail: 'Occurrence total unavailable',
  };
  if (!row) return unavailable;
  if (text(row.subjectKind) === 'COMPOSITE') {
    const when = text(row.latestReadingAt) || text(row.completedAt);
    return {
      value: text(row.stateChipLabel) || 'Open',
      detail: when ? `Latest occurrence: ${when}` : 'Latest occurrence unavailable',
      facts: [row.location, text(row.resultLabel)].filter((item) => item && item !== '—'),
      status: text(row.stateChipLabel) || 'Open',
      statusKind: 'status',
      tone: 'default',
    };
  }
  const headline = currentOccurrenceHeadline(row);
  const incomplete = coverageIncompleteFromRow(row);
  const measurementStatus = measurementStatusChip(row);
  const facts = nowCompactFacts(row, context);
  const mutedFacts = nowMutedFacts(row, context);
  const state = text(row.measurementState).toLowerCase();
  const noOperationalCount = (
    row.measurementValue == null
    && !text(row.supportingActivityLabel)
  );
  if (noOperationalCount && (
    !headline
    || /unavailable|never measured|not currently/i.test(state)
    || /not available|unavailable/i.test(text(row.resultLabel))
  )) {
    return {
      ...unavailable,
      facts,
      mutedFacts,
      status: measurementStatus || undefined,
      statusKind: measurementStatus ? 'measurement' : undefined,
      tone: 'amber',
    };
  }
  const measuredThrough = measuredThroughFact(row, context?.timeZone) || 'Measured through unavailable';
  if (headline === overviewV2OccurrenceHeadline(0) || row.measurementValue === 0) {
    return {
      value: overviewV2OccurrenceHeadline(0),
      detail: measuredThrough,
      facts,
      mutedFacts,
      status: measurementStatus,
      statusKind: measurementStatus ? 'measurement' : undefined,
      tone: incomplete ? 'amber' : 'default',
    };
  }
  if (headline) {
    return {
      value: headline,
      detail: measuredThrough,
      facts,
      mutedFacts,
      status: measurementStatus,
      statusKind: measurementStatus ? 'measurement' : undefined,
      tone: incomplete || /stale|delayed/i.test(state) ? 'amber' : 'default',
    };
  }
  if (row.cardShape === 'completed_with_result') {
    return { value: 'Occurrence total unavailable', detail: measuredThrough, facts, mutedFacts };
  }
  return {
    value: measurementStatus || (/threshold exceeded|needs attention/i.test(text(row.stateChipLabel)) ? 'Not currently measuring' : (row.stateChipLabel || 'Not currently measuring')),
    detail: 'Occurrence total unavailable',
    facts: facts.length ? facts : [row.bodyLine || row.coverageLabel].filter(Boolean),
    mutedFacts,
    status: measurementStatus || undefined,
    statusKind: measurementStatus ? 'measurement' : undefined,
    tone: incomplete ? 'amber' : 'default',
  };
}

const FORBIDDEN_CURRENT_ACTIVITY_COPY = /finalized|complete|reviewed|measured rate|coverage complete|claim-bearing/i;

function parseInstantMs(value: unknown): number | null {
  const raw = text(value);
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

function isoInstant(value: unknown): string | null {
  const raw = text(value);
  if (!/^\d{4}-\d{2}-\d{2}T/.test(raw)) return null;
  return parseInstantMs(raw) == null ? null : raw;
}

const MONTH_INDEX: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

function normalizeFinalizedClockText(value: unknown): string {
  return text(value)
    .replace(/[\u00a0\u202f\u2007\u2009]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^(?:measurements? through|measured through)\s+/i, '')
    .trim();
}

function isBlankFinalizedClock(raw: string): boolean {
  return !raw || raw === '—' || raw === '–' || raw === '-' || /^unavailable$/i.test(raw);
}

/** Parse formatSiteDateTime / formatCustomerTime clocks: "Aug 23, 8:15 PM" or "8:15 PM". */
function parseSiteClockLabel(
  raw: string,
  timeZone?: string | null,
): { ms: number; token: string } | null {
  const match = raw.trim().match(
    /^(?:([A-Za-z]{3})\.?\s+(\d{1,2})(?:,\s*(\d{4}))?(?:,|\s+at)?\s+)?(\d{1,2}):(\d{2})\s*(a\.?m\.?|p\.?m\.?)(?:\s+(?:UTC|GMT|[A-Z]{2,5}))?$/i,
  );
  if (!match) return null;
  const monthIdx = match[1] ? MONTH_INDEX[match[1].slice(0, 3).toLowerCase()] : 0;
  if (match[1] && monthIdx == null) return null;
  const year = match[3] ? Number(match[3]) : new Date().getUTCFullYear();
  const day = match[2] ? Number(match[2]) : 1;
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const period = match[6].replace(/\./g, '').toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  if (match[1] && timeZone) {
    const local = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const iso = parseSiteDateTimeLocalInput(local, timeZone);
    if (iso) {
      const ms = Date.parse(iso);
      if (Number.isFinite(ms)) return { ms, token: iso };
    }
  }
  return {
    ms: Date.UTC(year, match[1] ? monthIdx : 0, day, hour, minute),
    token: raw.trim(),
  };
}

function parseRfc1123Gmt(raw: string): { ms: number; token: string } | null {
  const match = raw.trim().match(
    /^(?:[A-Za-z]{3},?\s+)?(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?\s+(?:GMT|UTC)$/i,
  );
  if (!match) return null;
  const monthIdx = MONTH_INDEX[match[2].slice(0, 3).toLowerCase()];
  if (monthIdx == null) return null;
  const ms = Date.UTC(
    Number(match[3]),
    monthIdx,
    Number(match[1]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6] || 0),
  );
  if (!Number.isFinite(ms)) return null;
  return { ms, token: new Date(ms).toISOString() };
}

function parseFinalizedThroughInstant(
  value: unknown,
  timeZone?: string | null,
): { ms: number; token: string } | null {
  const raw = normalizeFinalizedClockText(value);
  if (isBlankFinalizedClock(raw)) return null;
  const iso = isoInstant(raw);
  if (iso) {
    const ms = parseInstantMs(iso);
    return ms == null ? null : { ms, token: iso };
  }
  const rfc = parseRfc1123Gmt(raw);
  if (rfc) return rfc;
  if (/\b(GMT|UTC)\b/i.test(raw) || /^(?:[A-Za-z]{3},?\s+)?\d{1,2}\s+[A-Za-z]{3}\s+\d{4}\b/.test(raw)) {
    const ms = Date.parse(raw.replace(/\bUTC\b/i, 'GMT'));
    if (Number.isFinite(ms)) return { ms, token: new Date(ms).toISOString() };
  }
  return parseSiteClockLabel(raw, timeZone);
}

/**
 * Latest finalized safety-condition OM end among Overview condition rows.
 * Same clocks the Last Completed rows already show: currentThroughAt, then
 * currentThrough, then completedAt. Never queryAsOf, Current Activity,
 * Camera Measurement, or Recurring.
 */
export function resolveOverviewV2FinalizedConditionMeasuredThrough(
  rows?: Array<{
    currentThroughAt?: string | null;
    currentThrough?: string | null;
    completedAt?: string | null;
    queryAsOf?: string | null;
    latestQualifiedAt?: string | null;
    lastCompleteEnd?: string | null;
  }> | null,
  timeZone?: string | null,
): string | null {
  let latest: string | null = null;
  let latestMs = Number.NEGATIVE_INFINITY;
  for (const row of rows || []) {
    const parsed = parseFinalizedThroughInstant(row.currentThroughAt, timeZone)
      || parseFinalizedThroughInstant(row.currentThrough, timeZone)
      || parseFinalizedThroughInstant(row.completedAt, timeZone);
    if (!parsed || parsed.ms < latestMs) continue;
    latestMs = parsed.ms;
    latest = parsed.token;
  }
  return latest;
}

function currentActivityAsOfIso(
  activity: SiteConditionCurrentActivity,
  now?: Date | string | null,
): string | null {
  if (now instanceof Date) return now.toISOString();
  if (text(now)) return text(now);
  return text(activity.queryAsOf) || text(activity.windowStart) || null;
}

function currentActivityWindowStartIso(
  activity: SiteConditionCurrentActivity,
  timeZone?: string | null,
  now?: Date | string | null,
): string | null {
  const asOf = currentActivityAsOfIso(activity, now);
  return openSitePt15mWindowStart(asOf, timeZone) || text(activity.windowStart) || null;
}

function currentActivityWindowLabel(
  windowStart: string | null,
  timeZone?: string | null,
): string {
  const start = formatSiteTime(windowStart, timeZone);
  if (start) return `${start}–now`;
  return 'Window in progress';
}

function occurrenceCountInCurrentWindow(
  activity: SiteConditionCurrentActivity,
): number | null {
  return activity.occurrenceCountSoFar;
}

function currentActivityHeadline(
  activity: SiteConditionCurrentActivity | null | undefined,
): string {
  if (!activity || activity.availability === 'unavailable' || activity.occurrenceCountSoFar == null) {
    return 'Current activity unavailable';
  }
  if (activity.availability === 'no_activity' || activity.occurrenceCountSoFar === 0) {
    return 'No qualifying activity observed';
  }
  return 'Activity observed';
}

export function currentActivityStage(
  activity: SiteConditionCurrentActivity | null | undefined,
  context?: { timeZone?: string | null; now?: Date | string | null },
): OverviewV2Stage {
  const timeZone = context?.timeZone;
  const windowStart = activity
    ? currentActivityWindowStartIso(activity, timeZone, context?.now)
    : null;
  const window = currentActivityWindowLabel(windowStart, timeZone);
  const headline = currentActivityHeadline(activity);
  if (!activity || activity.availability === 'unavailable' || activity.occurrenceCountSoFar == null) {
    return {
      value: headline,
      detail: window,
      status: 'Provisional',
      statusKind: 'status',
      tone: 'amber',
    };
  }
  const latest = formatSiteTime(activity.latestQualifiedAt, timeZone);
  const openDetail = latest ? `Window open · Latest activity ${latest}` : 'Window open';
  if (activity.availability === 'no_activity' || activity.occurrenceCountSoFar === 0) {
    return {
      value: headline,
      detail: 'Window open',
      mutedFacts: [window],
      status: 'Window in progress',
      statusKind: 'status',
    };
  }
  return {
    value: headline,
    detail: openDetail,
    mutedFacts: [window],
    status: 'Window in progress',
    statusKind: 'status',
  };
}

export function currentActivityCopyIsAllowed(stage: OverviewV2Stage): boolean {
  const blob = `${stage.value} ${stage.detail} ${(stage.facts || []).join(' ')} ${(stage.mutedFacts || []).join(' ')} ${stage.status || ''}`;
  return !FORBIDDEN_CURRENT_ACTIVITY_COPY.test(blob) && !/\bUTC\b/i.test(blob);
}

const PT15M_MS = 15 * 60 * 1000;

export type OverviewV2NowTimelineMark = {
  ratio: number;
  at: string;
};

export type OverviewV2NowTimeline = {
  kind: 'known_times' | 'count_only' | 'empty' | 'unavailable';
  marks: OverviewV2NowTimelineMark[];
  note: string | null;
};

function nowInstantMs(now?: Date | string | null, fallback?: string | null): number | null {
  if (now instanceof Date) return now.getTime();
  return parseInstantMs(now) ?? parseInstantMs(fallback);
}

/** Plot only known qualified_at instants in the open PT15M. Never invent marks from a count. */
export function currentActivityTimeline(
  activity: SiteConditionCurrentActivity | null | undefined,
  context?: { timeZone?: string | null; now?: Date | string | null },
): OverviewV2NowTimeline {
  if (!activity || activity.availability === 'unavailable' || activity.occurrenceCountSoFar == null) {
    return { kind: 'unavailable', marks: [], note: 'Occurrence times unavailable' };
  }
  const windowStart = currentActivityWindowStartIso(activity, context?.timeZone, context?.now);
  const startMs = parseInstantMs(windowStart);
  const endMs = nowInstantMs(context?.now, activity.queryAsOf);
  const count = occurrenceCountInCurrentWindow(activity) || 0;
  if (!startMs || endMs == null) {
    return {
      kind: count > 0 ? 'count_only' : 'empty',
      marks: [],
      note: count > 0
        ? 'Occurrence times are not available for this window'
        : null,
    };
  }
  const span = Math.max(endMs - startMs, 1);
  const boundedSpan = Math.min(span, PT15M_MS);
  const marks: OverviewV2NowTimelineMark[] = [];
  const qualifiedMs = parseInstantMs(activity.latestQualifiedAt);
  if (qualifiedMs != null && qualifiedMs >= startMs && qualifiedMs <= startMs + PT15M_MS && qualifiedMs <= endMs) {
    marks.push({
      ratio: Math.min(1, Math.max(0, (qualifiedMs - startMs) / boundedSpan)),
      at: activity.latestQualifiedAt as string,
    });
  }
  if (count <= 0) {
    return { kind: 'empty', marks: [], note: null };
  }
  if (!marks.length) {
    return {
      kind: 'count_only',
      marks: [],
      note: 'Occurrence times are not available for this window',
    };
  }
  return {
    kind: 'known_times',
    marks,
    note: count > marks.length
      ? 'Only the latest occurrence time is available — other times are not plotted'
      : null,
  };
}

export type OverviewV2RecurringChartBucket = {
  key: string;
  label: string;
  axisLabel: string | null;
  occurrences: number;
  usableHours: number | null;
  rate: number | null;
  plotValue: number;
  isPartial: boolean;
  start: string | null;
  end: string | null;
};

export type OverviewV2RecurringChart = {
  kind: 'rate_series' | 'occurrence_totals' | 'empty';
  grain: 'hour' | 'day';
  windowId: string;
  chartTitle: string;
  yAxisLabel: string;
  primaryValue: string | null;
  primaryUnit: string;
  secondaryOccurrenceLabel: string | null;
  coverageLabel: string | null;
  buckets: OverviewV2RecurringChartBucket[];
  peak: {
    startIndex: number;
    endIndex: number;
    label: string;
    /** UTC instant of the first selected bucket start. */
    start?: string | null;
    /** UTC instant of the last selected bucket end (exclusive). */
    end?: string | null;
  } | null;
  historyBuilding: boolean;
  gapNote: string | null;
};

/**
 * Expanded-view x-axis copy for the same Recurring ticks.
 * Does not change which hours are plotted or rate/coverage semantics.
 * Hour ticks always keep AM/PM; a date is appended only when the site-local day changes.
 */
export function recurringChartExpandedAxisLabel(
  bucket: Pick<OverviewV2RecurringChartBucket, 'axisLabel' | 'label' | 'start'>,
  options?: {
    grain?: 'hour' | 'day';
    previousDateLabel?: string | null;
    timeZone?: string | null;
  },
): { label: string | null; dateLabel: string | null } {
  if (!bucket.axisLabel) {
    return { label: null, dateLabel: options?.previousDateLabel || null };
  }
  const grain = options?.grain || 'hour';
  if (grain !== 'hour') {
    return { label: bucket.axisLabel, dateLabel: options?.previousDateLabel || null };
  }
  const clock = /\b(AM|PM)\b/i.test(bucket.label) ? bucket.label : bucket.axisLabel;
  const dateLabel = bucket.start ? (formatSiteMonthDay(bucket.start, options?.timeZone) || null) : null;
  const previous = options?.previousDateLabel || null;
  if (dateLabel && dateLabel !== previous) {
    return { label: `${clock} ${dateLabel}`, dateLabel };
  }
  return { label: clock, dateLabel: previous || dateLabel };
}

type RecurringChartSourceBucket = {
  occurrences: number;
  usableHours: number | null;
  expectedHours: number | null;
  start: string | null;
  end: string | null;
  isPartial: boolean;
  resolution: string;
  measurementMissing?: boolean;
};

function isOneDayRecurringWindow(windowId?: string | null): boolean {
  return windowId === 'rolling_24h' || windowId === 'rolling_24h' || windowId === 'rolling_1d';
}

function formatRecurringRate(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value >= 10 ? value.toFixed(1) : value.toFixed(2);
}

/** Headline rate is always count / hours when both exist. Reject a mismatched API rate. */
export function coherentRecurringRate(params: {
  occurrences: number | null | undefined;
  usableHours: number | null | undefined;
  apiRate?: number | null;
}): number | null {
  const count = typeof params.occurrences === 'number' && Number.isFinite(params.occurrences)
    ? params.occurrences
    : null;
  const hours = typeof params.usableHours === 'number' && params.usableHours > 0
    ? params.usableHours
    : null;
  if (count == null || hours == null) return null;
  const derived = count / hours;
  const apiRate = typeof params.apiRate === 'number' && Number.isFinite(params.apiRate)
    ? params.apiRate
    : null;
  if (apiRate != null) {
    const scale = Math.max(Math.abs(derived), 1e-9);
    if (Math.abs(apiRate - derived) / scale > 0.005) {
      // Stale bundle / reused policy row / cached query — never paint the mismatched rate.
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('overview recurring rate mismatch; using count/hours', {
          occurrences: count,
          usableHours: hours,
          derived,
          apiRate,
        });
      }
    }
  }
  return derived;
}

function zonedDateParts(iso: string, timeZone?: string | null): {
  year: string;
  month: string;
  day: string;
  hour: number;
} | null {
  const raw = text(iso);
  if (!raw) return null;
  const local = formatSiteDateTimeLocalInput(raw, timeZone);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(local)) {
    const hour = Number(local.slice(11, 13));
    if (Number.isFinite(hour)) {
      return {
        year: local.slice(0, 4),
        month: local.slice(5, 7),
        day: local.slice(8, 10),
        hour,
      };
    }
  }
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  const zone = text(timeZone) || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
    }).formatToParts(new Date(ms));
    const read = (type: string) => parts.find((part) => part.type === type)?.value || '';
    const hour = Number(read('hour'));
    if (!Number.isFinite(hour)) return null;
    return {
      year: read('year'),
      month: read('month'),
      day: read('day'),
      hour,
    };
  } catch {
    return null;
  }
}

function siteHourKey(parts: { year: string; month: string; day: string; hour: number }): string {
  return `${parts.year}-${parts.month}-${parts.day}T${String(parts.hour).padStart(2, '0')}`;
}

function siteHourStartMs(iso: string, timeZone?: string | null): number | null {
  const parts = zonedDateParts(iso, timeZone);
  if (!parts) {
    const parsed = Date.parse(iso);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const naive = `${parts.year}-${parts.month}-${parts.day}T${String(parts.hour).padStart(2, '0')}:00`;
  const utc = parseSiteDateTimeLocalInput(naive, timeZone);
  const ms = utc ? Date.parse(utc) : Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function emptyHourSource(startIso: string): RecurringChartSourceBucket {
  return {
    occurrences: 0,
    usableHours: null,
    expectedHours: null,
    start: startIso,
    end: null,
    isPartial: false,
    resolution: 'PT1H',
    measurementMissing: true,
  };
}

function hasUsableCameraTime(usableHours: number | null | undefined): boolean {
  return typeof usableHours === 'number' && Number.isFinite(usableHours) && usableHours > 0;
}

const CLOCK_HOUR_TOLERANCE_MS = 1000;

/**
 * Next civil hour after hourStartMs. One elapsed hour is enough except when a
 * fall-back hour repeats; then the probe steps to the following distinct hour.
 * Spring-forward skips the missing hour because +1 elapsed hour lands on the
 * next existing civil hour.
 */
function nextSiteClockHourStartMs(hourStartMs: number, timeZone?: string | null): number | null {
  for (const elapsedMinutes of [60, 120]) {
    const probed = siteHourStartMs(
      new Date(hourStartMs + elapsedMinutes * 60 * 1000).toISOString(),
      timeZone,
    );
    if (probed != null && probed > hourStartMs + CLOCK_HOUR_TOLERANCE_MS) return probed;
  }
  return null;
}

/**
 * A clock hour is complete when its measurement span is the full site-local
 * hour. A rolling window that starts or ends mid-hour does not. Usable time
 * below the expected hour is a coverage gap and still counts as complete.
 */
function spansCompleteSiteClockHour(
  bucket: { start: string | null; end: string | null },
  timeZone?: string | null,
): boolean {
  if (!bucket.start || !bucket.end) return false;
  const startMs = Date.parse(bucket.start);
  const endMs = Date.parse(bucket.end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return false;
  const hourStart = siteHourStartMs(bucket.start, timeZone);
  if (hourStart == null || Math.abs(startMs - hourStart) > CLOCK_HOUR_TOLERANCE_MS) return false;
  const hourEnd = nextSiteClockHourStartMs(hourStart, timeZone);
  if (hourEnd == null) return false;
  return Math.abs(endMs - hourEnd) <= CLOCK_HOUR_TOLERANCE_MS;
}

function siteHourClockLabel(iso: string, timeZone?: string | null): string {
  const parts = zonedDateParts(iso, timeZone);
  if (!parts) return formatSiteTime(iso, timeZone) || iso;
  const hour = parts.hour;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const clock = hour % 12 === 0 ? 12 : hour % 12;
  return `${clock} ${suffix}`;
}

/** One UTC conversion for peak bounds. Bucket instants may already carry an offset. */
export function peakBoundUtc(iso: string | null | undefined): string | null {
  const raw = text(iso);
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

function peakHourRangeLabel(startIso: string, endIso: string, timeZone?: string | null): string {
  const start = siteHourClockLabel(startIso, timeZone);
  const end = siteHourClockLabel(endIso, timeZone);
  const startMeridian = /\bAM\b/.test(start) ? 'AM' : (/\bPM\b/.test(start) ? 'PM' : '');
  const endMeridian = /\bAM\b/.test(end) ? 'AM' : (/\bPM\b/.test(end) ? 'PM' : '');
  if (startMeridian && startMeridian === endMeridian) {
    return `Peak: ${start.replace(/\s*(AM|PM)$/, '')}–${end}`;
  }
  return `Peak: ${start}–${end}`;
}

function sourceBucketsFromRow(row: OverviewPeriodConditionRow): RecurringChartSourceBucket[] {
  if (Array.isArray(row.activityBuckets) && row.activityBuckets.length) {
    return row.activityBuckets
      .filter((bucket) => Number.isFinite(bucket.occurrences))
      .map((bucket) => ({
        occurrences: bucket.occurrences,
        usableHours: bucket.usableHours,
        expectedHours: bucket.expectedHours,
        start: bucket.start,
        end: bucket.end,
        isPartial: bucket.isPartial,
        resolution: bucket.resolution,
      }));
  }
  const series = Array.isArray(row.activitySeries)
    ? row.activitySeries.filter((value) => typeof value === 'number' && Number.isFinite(value))
    : [];
  return series.map((occurrences) => ({
    occurrences,
    usableHours: null,
    expectedHours: null,
    start: null,
    end: null,
    isPartial: false,
    resolution: '',
  }));
}

function resolutionMinutes(resolution: string): number | null {
  const match = String(resolution || '').trim().toUpperCase().match(/^PT(\d+)M$/);
  if (!match) return null;
  const minutes = Number(match[1]);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

function mergeSourceBuckets(items: RecurringChartSourceBucket[]): RecurringChartSourceBucket {
  const usableValues = items
    .map((item) => item.usableHours)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0);
  const expectedValues = items
    .map((item) => item.expectedHours)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const starts = items.map((item) => item.start).filter(Boolean) as string[];
  const ends = items.map((item) => item.end).filter(Boolean) as string[];
  return {
    occurrences: items.reduce((sum, item) => sum + item.occurrences, 0),
    usableHours: usableValues.length === items.length
      ? usableValues.reduce((sum, value) => sum + value, 0)
      : null,
    expectedHours: expectedValues.length === items.length
      ? expectedValues.reduce((sum, value) => sum + value, 0)
      : null,
    start: starts[0] || null,
    end: ends[ends.length - 1] || null,
    isPartial: items.some((item) => item.isPartial),
    resolution: items[0]?.resolution || '',
    measurementMissing: items.length > 0 && items.every((item) => item.measurementMissing === true),
  };
}

function aggregateRecurringBuckets(
  sources: RecurringChartSourceBucket[],
  grain: 'hour' | 'day',
  timeZone?: string | null,
): { buckets: RecurringChartSourceBucket[]; sequential: boolean } {
  if (!sources.length) return { buckets: [], sequential: false };
  const keyed = new Map<string, RecurringChartSourceBucket[]>();
  let missingTime = 0;
  sources.forEach((source, index) => {
    const parts = source.start ? zonedDateParts(source.start, timeZone) : null;
    if (!parts) {
      missingTime += 1;
      const fallback = grain === 'hour' ? `seq-hour-${index}` : `seq-day-${index}`;
      keyed.set(fallback, [...(keyed.get(fallback) || []), source]);
      return;
    }
    const key = grain === 'hour'
      ? `${parts.year}-${parts.month}-${parts.day}T${String(parts.hour).padStart(2, '0')}`
      : `${parts.year}-${parts.month}-${parts.day}`;
    keyed.set(key, [...(keyed.get(key) || []), source]);
  });
  if (missingTime === sources.length) {
    if (grain === 'hour') {
      const minutes = resolutionMinutes(sources[0]?.resolution || '');
      const chunk = minutes && minutes < 60 && 60 % minutes === 0
        ? (60 / minutes)
        : (sources.length > 24 && sources.length % 4 === 0 ? 4 : 1);
      const grouped: RecurringChartSourceBucket[] = [];
      for (let index = 0; index < sources.length; index += chunk) {
        grouped.push(mergeSourceBuckets(sources.slice(index, index + chunk)));
      }
      return { buckets: grouped, sequential: true };
    }
    return { buckets: sources.map((source) => mergeSourceBuckets([source])), sequential: true };
  }
  if (grain === 'hour' && missingTime === 0) {
    const hourStarts = sources
      .map((source) => (source.start ? siteHourStartMs(source.start, timeZone) : null))
      .filter((ms): ms is number => ms != null);
    if (hourStarts.length) {
      const minMs = Math.min(...hourStarts);
      const maxMs = Math.max(...hourStarts);
      let guard = 0;
      for (let ms = minMs; ms <= maxMs && guard < 48; ms += 3600000, guard += 1) {
        const iso = new Date(ms).toISOString();
        const parts = zonedDateParts(iso, timeZone);
        if (!parts) continue;
        const key = siteHourKey(parts);
        if (!keyed.has(key)) keyed.set(key, [emptyHourSource(iso)]);
      }
    }
  }
  return {
    buckets: Array.from(keyed.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([, items]) => mergeSourceBuckets(items)),
    sequential: missingTime > 0,
  };
}

function sparseAxisLabel(
  bucket: RecurringChartSourceBucket,
  index: number,
  count: number,
  grain: 'hour' | 'day',
  sequential: boolean,
  timeZone?: string | null,
): string | null {
  if (grain === 'hour' && !sequential && bucket.start) {
    const parts = zonedDateParts(bucket.start, timeZone);
    if (parts && parts.hour % 4 === 0) return siteHourClockLabel(bucket.start, timeZone);
    if (index === 0 || index === count - 1) return siteHourClockLabel(bucket.start, timeZone);
    return null;
  }
  const show = index === 0 || index === count - 1 || (count > 2 && index % Math.max(1, Math.round((count - 1) / 6)) === 0);
  if (!show) return null;
  if (sequential || !bucket.start) {
    return grain === 'hour' ? String(index) : `D${index + 1}`;
  }
  return formatSiteMonthDay(bucket.start, timeZone) || bucket.start;
}

function chartBucketLabel(
  bucket: RecurringChartSourceBucket,
  index: number,
  grain: 'hour' | 'day',
  sequential: boolean,
  timeZone?: string | null,
): string {
  if (sequential || !bucket.start) {
    return grain === 'hour' ? `Hour ${index + 1}` : `Day ${index + 1}`;
  }
  if (grain === 'hour') return siteHourClockLabel(bucket.start, timeZone);
  return formatSiteMonthDay(bucket.start, timeZone) || bucket.start;
}

function peakFromBuckets(
  buckets: OverviewV2RecurringChartBucket[],
  grain: 'hour' | 'day',
  sequential: boolean,
  timeZone?: string | null,
): OverviewV2RecurringChart['peak'] {
  if (buckets.length < 1) return null;
  let bestStart = 0;
  let bestEnd = 0;
  let bestSum = -1;
  const considerWidth = (width: number) => {
    for (let index = 0; index <= buckets.length - width; index += 1) {
      const end = index + width - 1;
      const slice = buckets.slice(index, end + 1);
      if (!slice.every((bucket) => hasUsableCameraTime(bucket.usableHours))) continue;
      // A labeled clock range is the complete site-local hour or hours.
      // A rolling window that starts or ends mid-hour is not that hour.
      // Coverage gaps inside a full clock hour stay eligible.
      if (
        grain === 'hour'
        && !sequential
        && !slice.every((bucket) => spansCompleteSiteClockHour(bucket, timeZone))
      ) {
        continue;
      }
      const sum = slice.reduce((total, bucket) => total + bucket.plotValue, 0);
      if (sum > bestSum) {
        bestSum = sum;
        bestStart = index;
        bestEnd = end;
      }
    }
  };
  considerWidth(Math.min(2, buckets.length));
  if (bestSum <= 0) considerWidth(1);
  if (bestSum <= 0) return null;
  const startBucket = buckets[bestStart];
  const endBucket = buckets[bestEnd];
  let label = `Peak: ${startBucket.label}`;
  if (bestEnd !== bestStart) {
    if (!sequential && grain === 'hour' && startBucket.start && endBucket.end) {
      label = peakHourRangeLabel(startBucket.start, endBucket.end, timeZone);
    } else if (!sequential && grain === 'day' && startBucket.start && endBucket.start) {
      label = `Peak: ${formatSiteMonthDay(startBucket.start, timeZone)}–${formatSiteMonthDay(endBucket.start, timeZone)}`;
    } else {
      label = `Peak: ${startBucket.label}–${endBucket.label}`;
    }
  } else if (!sequential && grain === 'hour' && startBucket.start && startBucket.end) {
    label = peakHourRangeLabel(startBucket.start, startBucket.end, timeZone);
  } else if (!sequential && grain === 'day' && startBucket.start) {
    label = `Peak: ${formatSiteMonthDay(startBucket.start, timeZone)}`;
  }
  return {
    startIndex: bestStart,
    endIndex: bestEnd,
    label,
    start: peakBoundUtc(startBucket.start),
    end: peakBoundUtc(endBucket.end),
  };
}

function recurringHourChartTitle(
  buckets: Array<{ start: string | null; end: string | null }>,
  timeZone?: string | null,
): string {
  const days = new Set(
    buckets
      .flatMap((bucket) => [bucket.start, bucket.end])
      .map((iso) => formatSiteMonthDay(iso, timeZone))
      .filter(Boolean),
  );
  if (days.size === 1) return 'Today – by hour';
  return 'Last 24 hours – by hour';
}

/**
 * Recurring bucketed rate chart from existing period totals / OM buckets.
 * Do not derive occupancy duration from P/F/S. Do not invent per-hour usable hours.
 */
export function recurringOccurrenceChart(
  row: OverviewPeriodConditionRow | null | undefined,
  options?: { windowId?: string | null; timeZone?: string | null },
): OverviewV2RecurringChart {
  const windowId = text(options?.windowId) || 'rolling_24h';
  const timeZone = options?.timeZone;
  const grain: 'hour' | 'day' = isOneDayRecurringWindow(windowId) ? 'hour' : 'day';
  const chartTitle = grain === 'hour'
    ? 'Last 24 hours – by hour'
    : (windowId === 'rolling_7d' ? 'Last 7 days – by day' : 'Last 30 days – by day');
  const empty: OverviewV2RecurringChart = {
    kind: 'empty',
    grain,
    windowId,
    chartTitle,
    yAxisLabel: 'Rate (occurrences / usable hour)',
    primaryValue: null,
    primaryUnit: 'occurrences per usable hour',
    secondaryOccurrenceLabel: null,
    coverageLabel: null,
    buckets: [],
    peak: null,
    historyBuilding: true,
    gapNote: 'Recurring series unavailable',
  };
  if (!row) return empty;

  const windowTotal = typeof row.recordedOccurrenceTotal === 'number' && Number.isFinite(row.recordedOccurrenceTotal)
    ? row.recordedOccurrenceTotal
    : null;
  const windowUsable = typeof row.usableHours === 'number' && row.usableHours > 0 ? row.usableHours : null;
  const windowExpected = typeof row.expectedHours === 'number' && row.expectedHours > 0 ? row.expectedHours : null;
  const windowRate = coherentRecurringRate({
    occurrences: windowTotal,
    usableHours: windowUsable,
    apiRate: row.recurringRate,
  });
  const sources = sourceBucketsFromRow(row);
  const historyBuilding = !/has history/i.test(text(row.comparisonStatus))
    || (windowUsable != null && windowExpected != null && windowUsable < windowExpected);
  const secondaryOccurrenceLabel = overviewV2RecurringDenominator({
    occurrences: windowTotal,
    usableHours: windowUsable,
  });
  const intervalLabel = measuredIntervalsLabel(row);
  const coverageLabel = intervalLabel && !/unavailable/i.test(intervalLabel) ? intervalLabel : null;
  const primaryValue = windowRate != null ? formatRecurringRate(windowRate) : null;

  if (!sources.length && windowTotal == null) {
    return { ...empty, historyBuilding, primaryValue, secondaryOccurrenceLabel, coverageLabel };
  }
  if (!sources.length) {
    return {
      ...empty,
      kind: 'occurrence_totals',
      historyBuilding,
      primaryValue,
      secondaryOccurrenceLabel,
      coverageLabel,
      buckets: [{
        key: 'window',
        label: grain === 'hour' ? 'Selected day' : 'Selected window',
        axisLabel: grain === 'hour' ? 'Day' : 'Window',
        occurrences: windowTotal || 0,
        usableHours: windowUsable,
        rate: windowRate,
        plotValue: windowRate != null ? windowRate : 0,
        isPartial: historyBuilding,
        start: null,
        end: null,
      }],
      peak: null,
      gapNote: windowRate != null
        ? 'Selected window total. Per-period buckets are not available, so bars are not a per-hour usable rate series.'
        : 'Selected window total. Per-period buckets and usable hours are not available, so occurrence counts are not plotted as a rate.',
    };
  }

  const aggregated = aggregateRecurringBuckets(sources, grain, timeZone);
  const perBucketUsable = aggregated.buckets.filter((bucket) => hasUsableCameraTime(bucket.usableHours));
  const missingUsable = aggregated.buckets.some((bucket) => !hasUsableCameraTime(bucket.usableHours));
  const paddedMissingOm = aggregated.buckets.some((bucket) => bucket.measurementMissing === true);
  const trueNoUsableTime = aggregated.buckets.some(
    (bucket) => bucket.usableHours === 0 && bucket.measurementMissing !== true,
  );
  const plotRates = perBucketUsable.length > 0;
  const gapParts: string[] = [];
  if (paddedMissingOm) {
    gapParts.push(
      'Hours without operational-measurement buckets are not plotted as activity or as measured usable camera time.',
    );
  }
  if (trueNoUsableTime) {
    gapParts.push(
      plotRates
        ? 'Hours with no usable camera time are not plotted as activity. Occurrence counts are not substituted for a per-hour rate.'
        : 'Per-bucket usable hours are not available. Occurrence counts are not plotted as a rate. The headline rate uses total occurrences / window usable hours when both exist.',
    );
  } else if (missingUsable && !paddedMissingOm) {
    gapParts.push(
      plotRates
        ? 'Hours with no usable camera time are not plotted as activity. Occurrence counts are not substituted for a per-hour rate.'
        : 'Per-bucket usable hours are not available. Occurrence counts are not plotted as a rate. The headline rate uses total occurrences / window usable hours when both exist.',
    );
  }
  if (aggregated.sequential) {
    gapParts.push(
      grain === 'hour'
        ? 'Bucket timestamps are not available; hourly columns are sequential in the selected window, not labeled as site time of day.'
        : 'Bucket timestamps are not available; daily columns are sequential in the selected window.',
    );
  }

  const buckets: OverviewV2RecurringChartBucket[] = aggregated.buckets.map((bucket, index) => {
    const usable = typeof bucket.usableHours === 'number' && Number.isFinite(bucket.usableHours)
      ? bucket.usableHours
      : null;
    const rate = hasUsableCameraTime(usable) && usable != null ? bucket.occurrences / usable : null;
    const plotValue = rate != null ? rate : 0;
    const partial = hasUsableCameraTime(usable) && (
      bucket.isPartial
      || (
        index === aggregated.buckets.length - 1
        && usable != null
        && bucket.expectedHours != null
        && usable + 1e-9 < bucket.expectedHours
      )
    );
    return {
      key: `${grain}-${index}`,
      label: chartBucketLabel(bucket, index, grain, aggregated.sequential, timeZone),
      axisLabel: sparseAxisLabel(bucket, index, aggregated.buckets.length, grain, aggregated.sequential, timeZone),
      occurrences: bucket.occurrences,
      usableHours: usable,
      rate,
      plotValue,
      isPartial: partial,
      start: bucket.start,
      end: bucket.end,
    };
  });

  return {
    kind: plotRates ? 'rate_series' : 'occurrence_totals',
    grain,
    windowId,
    chartTitle: grain === 'hour' ? recurringHourChartTitle(buckets, timeZone) : chartTitle,
    yAxisLabel: 'Rate (occurrences / usable hour)',
    primaryValue,
    primaryUnit: 'occurrences per usable hour',
    secondaryOccurrenceLabel,
    coverageLabel,
    buckets,
    peak: peakFromBuckets(buckets, grain, aggregated.sequential, timeZone),
    historyBuilding,
    gapNote: gapParts.join(' ') || null,
  };
}

/** @deprecated Use recurringOccurrenceChart. Kept so existing Recurring cell imports compile during the swap. */
export function recurringActivitySparkline(
  row: OverviewPeriodConditionRow | null | undefined,
  options?: { windowId?: string | null; timeZone?: string | null },
): { kind: 'period_totals' | 'empty'; points: number[]; rateLabel: string | null; note: string | null } {
  const chart = recurringOccurrenceChart(row, options);
  return {
    kind: chart.kind === 'empty' ? 'empty' : 'period_totals',
    points: chart.buckets.map((bucket) => bucket.plotValue),
    rateLabel: chart.primaryValue ? `${chart.primaryValue} ${chart.primaryUnit}` : null,
    note: chart.gapNote || chart.yAxisLabel,
  };
}

function measuredIntervalsLabel(row: OverviewPeriodConditionRow): string {
  if (typeof row.measuredPeriods === 'number' && row.measuredPeriods > 0) {
    return `${row.measuredPeriods} measured intervals`;
  }
  const raw = text(row.measuredPeriodsLabel);
  if (/measured interval/i.test(raw)) {
    const match = raw.match(/\d[\d,]*\s+measured intervals?/i);
    return match ? match[0] : 'Measured intervals unavailable';
  }
  return 'Measured intervals unavailable';
}

function normalizeMeasuredActivityLabel(raw?: string | null, fallbackCount?: number | null): string {
  const textValue = text(raw);
  if (textValue) {
    return textValue
      .replace(/\bprovisional episodes recorded\b/gi, 'provisional measured occurrences recorded')
      .replace(/\bepisodes recorded\b/gi, 'measured occurrences recorded');
  }
  if (typeof fallbackCount === 'number') {
    return `${formatOverviewV2Count(fallbackCount)} measured occurrences recorded`;
  }
  return 'Measured occurrence total unavailable';
}

export function recurringPolicyStage(
  row: OverviewPeriodConditionRow | null,
  windowLabel?: string | null,
  _asOfLabel?: string | null,
): OverviewV2Stage {
  if (!row) return { value: 'Occurrence total unavailable', detail: 'No surfaced recurring review' };
  if (text(row.subjectKind) === 'COMPOSITE') {
    const count = typeof row.recordedOccurrenceTotal === 'number' ? row.recordedOccurrenceTotal : null;
    return {
      value: count != null ? `${count} occurrences` : 'Keeps occurring',
      detail: text(row.includedThroughAt) ? `Latest occurrence ${row.includedThroughAt}` : (text(windowLabel) || 'Observation period'),
      facts: [row.location].filter(Boolean),
      status: 'Keeps occurring',
      statusKind: 'status',
      tone: 'amber',
    };
  }
  const count = typeof row.recordedOccurrenceTotal === 'number' ? row.recordedOccurrenceTotal : null;
  const comparison = text(row.comparisonStatus).toLowerCase();
  const pattern = comparison.includes('pattern');
  const rawWindow = text(windowLabel) || 'selected window';
  const window = `${/^last\b/i.test(rawWindow) ? rawWindow : `Last ${rawWindow}`} · ${measuredIntervalsLabel(row)}`;
  const facts: string[] = [];
  const mutedFacts: string[] = [];
  const status = pattern ? 'Pattern recurring' : 'History building';
  const chart = recurringOccurrenceChart(row, { windowId: null });
  if (chart.primaryValue) {
    return {
      value: `${chart.primaryValue} ${chart.primaryUnit}`,
      detail: chart.secondaryOccurrenceLabel || window,
      facts,
      mutedFacts,
      status,
      statusKind: 'status',
      tone: pattern ? 'amber' : 'default',
    };
  }
  if (typeof count === 'number') {
    return {
      value: overviewV2RecurringDenominator({ occurrences: count, usableHours: row.usableHours })
        || normalizeMeasuredActivityLabel(row.recordedOccurrenceLabel, count),
      detail: window,
      facts,
      mutedFacts,
      status,
      statusKind: 'status',
      tone: pattern ? 'amber' : 'default',
    };
  }
  return {
    value: 'Occurrence total unavailable',
    detail: window,
    facts,
    mutedFacts,
    status,
    statusKind: 'status',
    tone: pattern ? 'amber' : 'default',
  };
}

function controlBaselineReason(label?: string | null): string | null {
  const raw = text(label);
  if (!raw) return null;
  if (/clean pre-control baseline/i.test(raw)) return 'no clean baseline';
  return raw;
}

function formatOverviewV2ControlDate(value?: string | null, timeZone?: string | null): string {
  const raw = text(value);
  if (!raw) return '';
  if (/GMT|UTC/i.test(raw) || /^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    return formatSiteDateTime(raw, timeZone) || raw;
  }
  return raw.replace(/,\s*/, ' ');
}

function presentOverviewV2ControlDate(date: string): string {
  if (/\d{1,2}:\d{2}/.test(date)) return date;
  return date.replace(/,\s*/, ' ');
}

export function controlPolicyStage(args: {
  linkedToControl: boolean;
  controlId: string | null;
  controlDateLabel: string | null | undefined;
  controlName?: string | null;
  recordedStatusLabel?: string | null;
  followUpState?: string | null;
  presentationKind?: string | null;
  basisDifferedLabel?: string | null;
  comparisonSides?: Array<{ label?: string | null; bodyLine?: string | null }> | null;
  followUpThroughLabel?: string | null;
  timeZone?: string | null;
}): OverviewV2Stage {
  if (!args.linkedToControl || !args.controlId) {
    return {
      value: 'No control recorded',
      detail: OVERVIEW_V2_EMPTY_CONTROL_DETAIL,
      status: 'No control recorded',
      statusKind: 'status',
    };
  }
  const recordedStatus = text(args.recordedStatusLabel);
  const date = formatOverviewV2ControlDate(args.controlDateLabel, args.timeZone);
  const followUp = text(args.followUpState).toLowerCase().replace(/_/g, ' ');
  const kind = text(args.presentationKind).toLowerCase();
  const notComparable = followUp.includes('not comparable') || kind === 'not_comparable';
  const followUpAvailable = notComparable || kind === 'comparison_issuable';
  const reason = notComparable ? controlBaselineReason(args.basisDifferedLabel) : null;
  const comparableOutcomes = kind === 'comparison_issuable'
    ? (args.comparisonSides || [])
      .map((side) => {
        const label = text(side.label);
        const body = text(side.bodyLine);
        if (!label || !body) return null;
        return `${label}: ${body}`;
      })
      .filter((line): line is string => Boolean(line))
    : [];
  const facts = [
    date ? `Recorded date: ${presentOverviewV2ControlDate(date)}` : null,
    recordedStatus && !followUpAvailable ? `Status: ${recordedStatus}` : null,
    followUpAvailable ? 'Follow-up: Measurement available' : null,
    notComparable ? 'Comparison: Not comparable' : null,
    reason ? `Reason: ${reason}` : null,
    ...comparableOutcomes.slice(-2),
    text(args.followUpThroughLabel)
      ? `Measured through: ${formatOverviewV2ControlDate(args.followUpThroughLabel, args.timeZone)}`
      : null,
  ].filter((line): line is string => Boolean(line));
  return {
    value: overviewV2ControlDisplayName(args.controlName) || 'Recorded control',
    detail: date
      ? `Recorded ${presentOverviewV2ControlDate(date)}`
      : (recordedStatus || 'Control recorded on the Safety Case'),
    facts,
    status: notComparable ? 'Not comparable' : (recordedStatus || null),
    statusKind: notComparable ? 'measurement' : undefined,
    tone: notComparable ? 'amber' : 'default',
  };
}

export function canonicalSafetyStory(args: {
  policyLabel: string;
  controlDateLabel?: string | null;
  timeZone?: string | null;
}): string {
  const date = formatOverviewV2ControlDate(args.controlDateLabel, args.timeZone) || 'the recorded date';
  return `${args.policyLabel} activity is recurring beyond the current measured window. A demonstration control marker was recorded ${date}. Follow-up measurement is available, but a clean pre-control baseline is unavailable, so before/after effectiveness cannot be compared.`;
}

export function composeZoneSafetyStory(args: {
  policyLabel: string;
  comparisonStatus?: string | null;
  linkedToControl: boolean;
  controlName?: string | null;
  controlDateLabel?: string | null;
  followUpState?: string | null;
  coverageIncomplete?: boolean;
  hasRecordedHistory?: boolean;
  timeZone?: string | null;
}): string {
  if (args.linkedToControl) {
    const named = overviewV2ControlDisplayName(args.controlName) || text(args.controlName);
    if (named && !/^demonstration control$/i.test(named) && !/^recorded control$/i.test(named)) {
      const date = formatOverviewV2ControlDate(args.controlDateLabel, args.timeZone);
      const policy = text(args.policyLabel) || 'This safety condition';
      const pattern = text(args.comparisonStatus).toLowerCase().includes('pattern');
      const history = pattern
        ? `${policy} activity is recurring beyond the current measured window.`
        : `${policy} activity is shown across the current and recorded windows.`;
      const recorded = date ? `${named} was recorded ${date}.` : `${named} is recorded.`;
      const comparable = text(args.followUpState).toLowerCase().includes('not comparable');
      const followUp = comparable
        ? 'Follow-up measurement is available, but a clean pre-control baseline is unavailable, so before/after effectiveness cannot be compared.'
        : (text(args.followUpState)
          ? 'Follow-up measurement is recorded.'
          : 'Follow-up measurement begins after this control.');
      return `${history} ${recorded} ${followUp}`;
    }
    return canonicalSafetyStory({
      policyLabel: args.policyLabel,
      controlDateLabel: args.controlDateLabel,
      timeZone: args.timeZone,
    });
  }
  const policy = text(args.policyLabel) || 'This safety condition';
  const pattern = text(args.comparisonStatus).toLowerCase().includes('pattern');
  const recordedHistory = args.hasRecordedHistory !== false && Boolean(text(args.comparisonStatus) || args.hasRecordedHistory);
  if (!args.linkedToControl) {
    if (args.coverageIncomplete && recordedHistory) {
      return `${policy} activity continues to be recorded across measured periods. Measurement coverage is incomplete, and no control is currently recorded for these conditions.`;
    }
    const history = pattern
      ? `${policy} activity has a recorded recurring pattern.`
      : `${policy} activity has recorded history.`;
    return `${history} No control is currently recorded for these conditions.`;
  }
  const date = formatOverviewV2ControlDate(args.controlDateLabel, args.timeZone);
  const control = text(args.controlName) || 'A control';
  const recorded = date ? `${control} was recorded ${date}.` : `${control} is recorded.`;
  const comparable = text(args.followUpState).toLowerCase().includes('not comparable');
  const history = pattern
    ? `${policy} activity is recurring beyond the current measured window.`
    : `${policy} activity is shown across the current and recorded windows.`;
  const followUp = comparable
    ? 'Follow-up measurement is available, but a clean pre-control baseline is unavailable, so before/after effectiveness cannot be compared.'
    : 'Follow-up measurement is recorded.';
  return `${history} ${recorded} ${followUp}`;
}

export function recurringReviewCount(rows: Array<{ comparisonStatus?: string | null }>): number {
  return rows.filter((row) => text(row.comparisonStatus).toLowerCase().includes('pattern')).length;
}

export function summaryAreaNames(titles: string[]): string {
  return titles.map((title) => title.replace(/^Warehouse\s+/i, '')).join(' and ') || 'No zones available';
}

export function customerCameraLabel(args: { cameraName?: string | null; location: string }): string {
  if (text(args.cameraName)) return text(args.cameraName);
  const place = args.location.replace(/\s+-\s+/g, ' ').replace(/ · /g, ' ').trim();
  return place ? `${place} Camera` : 'Area camera';
}

export function customerZoneLabel(args: { zoneName?: string | null; location: string }): string {
  const named = text(args.zoneName);
  if (named && /\s/.test(named) && !/^zone unavailable$/i.test(named)) return named;
  const cleaned = args.location
    .replace(/^Warehouse\s+/i, '')
    .replace(/\s+-\s+/g, ' ')
    .replace(/ · /g, ' ')
    .trim();
  const match = cleaned.match(/Room\s+([A-Z])\s+(.+)$/i);
  if (match) return `Room ${match[1].toUpperCase()} ${match[2].toLowerCase()} zone`;
  if (named && !/^zone unavailable$/i.test(named)) return named;
  return cleaned || 'This camera view';
}

export type OverviewV2SpatialPlace = {
  areaLabel: string;
  mountLabel: string | null;
};

export type OverviewV2MeasurementScopeKind = 'ENTIRE_CAMERA_VIEW' | 'ZONE_POLYGON';

export const OVERVIEW_V2_ENTIRE_CAMERA_VIEW_LABEL = 'Entire camera view';

/** Camera-card identity. Zone is never included. */
export type OverviewV2CameraIdentity = {
  siteId: string;
  deviceId: string | null;
  cameraId: string | null;
};

export type OverviewV2CameraMeasurementBinding = {
  scopeKind: 'ENTIRE_CAMERA_VIEW';
  scopeLabel: typeof OVERVIEW_V2_ENTIRE_CAMERA_VIEW_LABEL;
};

/**
 * Effective Measurement Definition adapter seam.
 * Polygon/rule stay null until that endpoint is integrated. Never fabricate.
 */
export type OverviewV2ConditionSpatialScope = {
  kind: OverviewV2MeasurementScopeKind;
  zoneId: string | null;
  polygon: null;
  rule: null;
  definitionAvailable: boolean;
};

export type OverviewV2ConditionOwnershipInput = {
  conditionId: string;
  label?: string | null;
  location: string;
  cameraId: string | null;
  deviceId?: string | null;
  zoneId?: string | null;
  areaId?: string | null;
  areaName?: string | null;
};

export type OverviewV2CameraGroup<T extends OverviewV2ConditionOwnershipInput = OverviewV2ConditionOwnershipInput> = {
  key: string;
  identity: OverviewV2CameraIdentity;
  location: string;
  areaId: string | null;
  areaName: string | null;
  cameraMeasurement: OverviewV2CameraMeasurementBinding;
  conditions: T[];
};

export type OverviewV2SpatialCameraInput = {
  key: string;
  location: string;
  cameraId: string | null;
  deviceId?: string | null;
  areaId?: string | null;
  areaName?: string | null;
  /** Condition/spatial metadata only. Must not drive area or camera-card grouping. */
  zoneId?: string | null;
};

export type OverviewV2CameraPlacement = {
  areaId?: string | null;
  areaName?: string | null;
  mountLabel?: string | null;
  deviceId?: string | null;
};

export const OVERVIEW_V2_UNASSIGNED_AREA_KEY = 'unassigned';
export const OVERVIEW_V2_UNASSIGNED_AREA_LABEL = 'Unassigned location';

export type OverviewV2SpatialAreaGroup<T extends OverviewV2SpatialCameraInput> = {
  key: string;
  areaId: string | null;
  areaLabel: string;
  cameras: T[];
};

export type OverviewV2SiteGroup<T extends OverviewV2SpatialCameraInput> = {
  siteId: string;
  areas: OverviewV2SpatialAreaGroup<T>[];
};

/**
 * Camera-card key: site_id + device_id + camera_id.
 * Zone is never part of this key. Incomplete camera identity fails closed
 * (conditionId discriminator) instead of merging via zone.
 */
export function overviewV2CameraCardKey(args: {
  siteId: string;
  deviceId?: string | null;
  cameraId?: string | null;
  conditionId?: string | null;
}): string {
  const siteId = text(args.siteId) || 'unavailable';
  const deviceId = text(args.deviceId);
  const cameraId = text(args.cameraId);
  const parts = [
    `site:${siteId}`,
    `device:${deviceId || 'unavailable'}`,
    `camera:${cameraId || 'unavailable'}`,
  ];
  if (!cameraId) {
    const conditionId = text(args.conditionId);
    if (conditionId) parts.push(`condition:${conditionId}`);
  }
  return parts.join('|');
}

export function overviewV2CameraMeasurementBinding(): OverviewV2CameraMeasurementBinding {
  return {
    scopeKind: 'ENTIRE_CAMERA_VIEW',
    scopeLabel: OVERVIEW_V2_ENTIRE_CAMERA_VIEW_LABEL,
  };
}

/** Never surface raw `_camera_scope` as a zone name. */
export function overviewV2PublicZoneId(zoneId?: string | null): string | null {
  const raw = text(zoneId);
  if (!raw || raw === '_camera_scope') return null;
  return raw;
}

/**
 * Known presentation mapping only. Does not invent polygon/rule from the backend.
 * Near miss: entire camera view (zone may later be applicability/gate).
 * Zone violation: polygon kind, definition unavailable until EMD is integrated.
 */
export function overviewV2ConditionSpatialScope(args: {
  incidentType?: string | null;
  zoneId?: string | null;
}): OverviewV2ConditionSpatialScope {
  const incident = text(args.incidentType).toLowerCase().replace(/-/g, '_');
  const zoneId = overviewV2PublicZoneId(args.zoneId);
  if (incident === 'zone_violation' || incident === 'zone_intrusion') {
    return {
      kind: 'ZONE_POLYGON',
      zoneId,
      polygon: null,
      rule: null,
      definitionAvailable: false,
    };
  }
  return {
    kind: 'ENTIRE_CAMERA_VIEW',
    zoneId,
    polygon: null,
    rule: null,
    definitionAvailable: true,
  };
}

export function overviewV2MeasurementScopeLabel(args: {
  incidentType?: string | null;
  zoneId?: string | null;
  zoneLabel?: string | null;
}): string {
  const scope = overviewV2ConditionSpatialScope({
    incidentType: args.incidentType,
    zoneId: args.zoneId,
  });
  if (scope.kind === 'ZONE_POLYGON') {
    const label = text(args.zoneLabel) || 'Configured zone';
    return `Measurement scope: ${label}`;
  }
  return `Measurement scope: ${OVERVIEW_V2_ENTIRE_CAMERA_VIEW_LABEL}`;
}

/** Split "Room B - Walkway" into area vs mount. Does not invent a site rollup. */
export function parseOverviewV2SpatialPlace(location: string): OverviewV2SpatialPlace {
  const raw = text(location);
  if (!raw) return { areaLabel: 'Monitored area', mountLabel: null };
  const parts = raw.replace(/\s+·\s+/g, ' - ').split(/\s+-\s+/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { areaLabel: parts[0], mountLabel: parts.slice(1).join(' - ') };
  }
  return { areaLabel: raw, mountLabel: null };
}

export function resolveOverviewV2CameraPlacement(
  camera: { cameraId?: string | null; deviceId?: string | null },
  placements?: Record<string, OverviewV2CameraPlacement>,
): OverviewV2CameraPlacement | undefined {
  return resolveIndexedCameraPlacement(placements, camera);
}

function resolveOverviewV2AreaPlace(
  camera: OverviewV2SpatialCameraInput,
  placements?: Record<string, OverviewV2CameraPlacement>,
): { areaId: string | null; areaLabel: string } {
  const placement = resolveOverviewV2CameraPlacement(camera, placements);
  const areaId = text(camera.areaId) || text(placement?.areaId) || null;
  const areaName = text(camera.areaName) || text(placement?.areaName);
  // Canonical Area only. Never derive grouping identity from location, zone, mount, or camera name.
  if (!areaId) {
    return { areaId: null, areaLabel: OVERVIEW_V2_UNASSIGNED_AREA_LABEL };
  }
  return { areaId, areaLabel: areaName || areaId };
}

export function normalizeOverviewV2PlaceToken(value: unknown): string {
  return text(value).toLowerCase().replace(/\s+/g, ' ');
}

export function overviewV2AreaGroupKey(areaId?: string | null): string {
  return text(areaId) || OVERVIEW_V2_UNASSIGNED_AREA_KEY;
}

/** Suppress mount when it duplicates the Area display_name. Case/whitespace only. */
export function overviewV2VisibleMountLabel(
  areaDisplayName?: string | null,
  mountLabel?: string | null,
): string | null {
  const mount = text(mountLabel);
  if (!mount) return null;
  if (normalizeOverviewV2PlaceToken(mount) === normalizeOverviewV2PlaceToken(areaDisplayName)) return null;
  return mount;
}

export function overviewV2AreaGroupCounts(cameras: Array<{ policies?: unknown[] }>): {
  cameraCount: number;
  conditionCount: number;
} {
  return {
    cameraCount: cameras.length,
    conditionCount: cameras.reduce((count, camera) => (
      count + (Array.isArray(camera.policies) ? camera.policies.length : 0)
    ), 0),
  };
}

export function overviewV2AreaCollapsedSummary(args: { cameraCount: number; conditionCount: number }): string {
  const cameras = args.cameraCount === 1 ? '1 camera' : `${args.cameraCount} cameras`;
  const conditions = args.conditionCount === 1 ? '1 active condition' : `${args.conditionCount} active conditions`;
  return `${cameras} · ${conditions}`;
}

export function overviewV2AreaNeedsAttention<T extends {
  policies?: Array<{ priority?: boolean; current?: { attentionRequired?: boolean } | null }>;
}>(cameras: T[]): boolean {
  return cameras.some((camera) => camera.policies?.some((policy) => (
    Boolean(policy.priority || policy.current?.attentionRequired)
  )));
}

export const OVERVIEW_V2_AREA_EXPANSION_STORAGE_VERSION = 'v2';

export function overviewV2DefaultExpandedAreaIds(
  groups: Array<{ key: string; needsAttention: boolean }>,
): string[] {
  if (groups.length <= 2) return groups.map((group) => group.key);
  const attention = groups.find((group) => group.needsAttention);
  return [(attention || groups[0]).key];
}

export function overviewV2AreaExpansionStorageKey(siteId: string): string {
  return `overview-v2-area-expanded:${OVERVIEW_V2_AREA_EXPANSION_STORAGE_VERSION}:${text(siteId) || 'site'}`;
}

export function readOverviewV2AreaExpansionState(siteId: string): Record<string, boolean> | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(overviewV2AreaExpansionStorageKey(siteId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Record<string, boolean>;
  } catch {
    return null;
  }
}

export function writeOverviewV2AreaExpansionState(siteId: string, state: Record<string, boolean>): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(overviewV2AreaExpansionStorageKey(siteId), JSON.stringify(state));
  } catch {
    // Ignore quota / private-mode failures; in-memory state still applies.
  }
}

export function mergeOverviewV2AreaExpansionState(
  groups: Array<{ key: string; needsAttention: boolean }>,
  stored: Record<string, boolean> | null,
): Record<string, boolean> {
  const defaults = new Set(overviewV2DefaultExpandedAreaIds(groups));
  const next: Record<string, boolean> = {};
  groups.forEach((group) => {
    if (stored && Object.prototype.hasOwnProperty.call(stored, group.key)) {
      next[group.key] = Boolean(stored[group.key]);
    } else {
      next[group.key] = defaults.has(group.key);
    }
  });
  return next;
}

/**
 * Group condition rows onto physical camera cards.
 * Identity is site + device + camera. Conditions stay attached by conditionId.
 * Does not mutate backend/domain records.
 */
export function groupOverviewV2ConditionsByCamera<T extends OverviewV2ConditionOwnershipInput>(
  siteId: string,
  rows: T[],
): OverviewV2CameraGroup<T>[] {
  const groups: OverviewV2CameraGroup<T>[] = [];
  const indexByKey = new Map<string, number>();
  rows.forEach((row) => {
    const cameraId = text(row.cameraId) || null;
    const deviceId = text(row.deviceId) || null;
    const key = overviewV2CameraCardKey({
      siteId,
      deviceId,
      cameraId,
      conditionId: row.conditionId,
    });
    const existing = indexByKey.get(key);
    if (existing == null) {
      indexByKey.set(key, groups.length);
      groups.push({
        key,
        identity: { siteId, deviceId, cameraId },
        location: row.location,
        areaId: text(row.areaId) || null,
        areaName: text(row.areaName) || null,
        cameraMeasurement: overviewV2CameraMeasurementBinding(),
        conditions: [row],
      });
      return;
    }
    groups[existing].conditions.push(row);
  });
  return groups;
}

/**
 * Presentation grouping only: Site children are operational areas; each camera card stays intact.
 * Grouping key is canonical area_id from camera placements. Label is display_name.
 * Fail closed to a single unassigned group when area_id is missing.
 * Does not merge conditions or camera-activity P/F/S across cameras.
 * Does not use zoneId, mount, camera name, or condition.location as area identity.
 */
export function groupOverviewV2AreasBySpatialScope<T extends OverviewV2SpatialCameraInput>(
  cameras: T[],
  placements?: Record<string, OverviewV2CameraPlacement>,
): OverviewV2SpatialAreaGroup<T>[] {
  const groups: OverviewV2SpatialAreaGroup<T>[] = [];
  const indexByKey = new Map<string, number>();
  cameras.forEach((camera) => {
    const place = resolveOverviewV2AreaPlace(camera, placements);
    const key = overviewV2AreaGroupKey(place.areaId);
    const existing = indexByKey.get(key);
    if (existing == null) {
      indexByKey.set(key, groups.length);
      groups.push({
        key,
        areaId: place.areaId,
        areaLabel: place.areaLabel,
        cameras: [camera],
      });
      return;
    }
    groups[existing].cameras.push(camera);
    if (place.areaId && place.areaLabel) groups[existing].areaLabel = place.areaLabel;
  });
  return groups;
}

export function groupOverviewV2OwnershipTree<T extends OverviewV2SpatialCameraInput>(
  siteId: string,
  cameras: T[],
  placements?: Record<string, OverviewV2CameraPlacement>,
): OverviewV2SiteGroup<T> {
  return {
    siteId,
    areas: groupOverviewV2AreasBySpatialScope(cameras, placements),
  };
}

export function sameScreenConditionIdentity(args: {
  conditionId: string;
  currentId?: string | null;
  recurringId?: string | null;
  attentionId?: string | null;
}): boolean {
  if (args.currentId && args.currentId !== args.conditionId) return false;
  if (args.recurringId && args.recurringId !== args.conditionId) return false;
  if (args.attentionId && args.attentionId !== args.conditionId) return false;
  return true;
}

const HUMAN_OPEN_WORK = new Set([
  'claim_review_incomplete',
  'since_control_comparability_failed',
  'control_recheck_unscheduled',
]);

export function classifyOverviewV2AttentionWork(items: OverviewV2AttentionItem[]): OverviewV2AttentionItem[] {
  const seen = new Set<string>();
  return items.map((item) => {
    const key = `${item.source}:${text(item.conditionId) || text(item.id)}`;
    if (!key || key.endsWith(':')) return { ...item, classification: 'STALE_WORK' as const };
    if (seen.has(key)) return { ...item, classification: 'DUPLICATE_SAME_WORK' as const };
    seen.add(key);
    return { ...item, classification: 'DISTINCT_VALID_WORK' as const };
  }).filter((item) => item.classification === 'DISTINCT_VALID_WORK');
}

function customerAttentionCopy(args: {
  label: string;
  location: string;
  attentionReason?: string | null;
  conditionId: string;
  decisionActions?: Array<{ id?: string | null; objectLabel?: string | null }>;
}): { reason: string; detail: string } {
  const label = overviewV2ConditionDisplayName(args.label) || 'Condition';
  const matched = (args.decisionActions || []).find((row) => {
    const id = text(row.id);
    return Boolean(id) && (id === args.conditionId || id.endsWith(`:${args.conditionId}`));
  });
  if (text(matched?.objectLabel)) {
    return { reason: text(matched?.objectLabel), detail: 'Work waiting on a person' };
  }
  const latch = text(args.attentionReason);
  if (/^immediate_policy$/i.test(latch)) {
    return { reason: `Ready for final review — ${label}`, detail: 'Work waiting on a person' };
  }
  if (/^recurring_policy$/i.test(latch)) {
    return { reason: `${label} requires review`, detail: 'Work waiting on a person' };
  }
  if (latch && !looksLikeLatchCode(latch)) {
    if (/near[-\s]?miss|danger[-\s]?zone|zone violation|restricted[-\s]?zone/i.test(latch)) {
      return { reason: `${label} requires review`, detail: 'Work waiting on a person' };
    }
    return { reason: latch, detail: 'Work waiting on a person' };
  }
  return {
    reason: `${label} requires review · ${args.location}`,
    detail: 'Work waiting on a person',
  };
}

function finitePendingCount(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

export function resolveOverviewV2PendingReviewCount(row?: {
  pendingReviewCount?: number | null;
  reviewSetId?: string | null;
  measurementValue?: number | null;
  supportingActivityLabel?: string | null;
  supportingActivityTotal?: number | null;
} | null): number | null {
  if (!row) return null;
  // Quantitative review copy is valid only for an enumerable review population.
  // Never promote OM NOW, supporting-activity totals, or parsed activity copy.
  if (!text(row.reviewSetId)) return null;
  return finitePendingCount(row.pendingReviewCount);
}

export function formatOverviewV2ReviewEpisodesCta(count: number): string {
  const n = Math.trunc(count);
  if (n === 1) return 'Review 1 observation';
  return `Review ${formatOverviewV2Count(n)} observations`;
}

export function resolveOverviewV2NextAction(args: {
  source: OverviewV2AttentionItem['source'];
  pendingReviewCount?: number | null;
  reviewSetId?: string | null;
  nextAction?: string | null;
  openWorkKind?: string | null;
  attentionReason?: string | null;
  casePath?: string | null;
  scanVerifyPath?: string | null;
}): { kind: OverviewV2NextActionKind; label: string } {
  const pending = resolveOverviewV2PendingReviewCount(args);
  const next = text(args.nextAction).toLowerCase();
  const hint = `${text(args.openWorkKind)} ${text(args.attentionReason)}`.toLowerCase();
  if (next === 'record_control' || /control_recheck_unscheduled|open control/.test(hint)) {
    return { kind: 'open_control', label: 'Open Control' };
  }
  if (next === 'prepare_signoff' || /claim_review_incomplete|sign[- ]?off|sign safety scan/.test(hint)) {
    return { kind: 'sign_safety_scan', label: 'Sign Safety Scan' };
  }
  if (args.source === 'coverage') {
    return { kind: 'view', label: 'View' };
  }
  const scanVerifyPath = text(args.scanVerifyPath);
  if (scanVerifyPath && next === 'verify_observations') {
    return { kind: 'verify_scan', label: 'Verify observations' };
  }
  const casePath = text(args.casePath);
  if (casePath) {
    return { kind: 'open_safety_case', label: 'Open Safety Case' };
  }
  if (pending != null) {
    return { kind: 'review_episodes', label: formatOverviewV2ReviewEpisodesCta(pending) };
  }
  if (next === 'verify_observations' || args.source === 'condition_attention' || args.source === 'current_condition') {
    return { kind: 'investigate', label: 'Review activity' };
  }
  if (args.source === 'open_work') {
    return { kind: 'investigate', label: 'Investigate' };
  }
  return { kind: 'investigate', label: 'Investigate' };
}

/** Key used to de-dupe the same next-action workflow across attention and zone rows. */
export function overviewV2NextActionWorkflowKey(args: {
  conditionId?: string | null;
  nextActionKind?: string | null;
  targetPath?: string | null;
}): string | null {
  const conditionId = text(args.conditionId);
  const kind = text(args.nextActionKind);
  if (!conditionId || !kind) return null;
  const path = text(args.targetPath);
  return path ? `${conditionId}::${kind}::${path}` : `${conditionId}::${kind}`;
}

export function overviewV2AttentionOwnsZoneReview(
  ownedKeys: Iterable<string>,
  args: { conditionId?: string | null; evidencePath?: string | null; casePath?: string | null },
): boolean {
  const conditionId = text(args.conditionId);
  const evidencePath = text(args.evidencePath);
  const casePath = text(args.casePath);
  if (!conditionId || (!evidencePath && !casePath)) return false;
  const keys = ownedKeys instanceof Set ? ownedKeys : new Set(ownedKeys);
  const reviewKinds: OverviewV2NextActionKind[] = [
    'review_episodes',
    'investigate',
    'sign_safety_scan',
    'open_control',
    'open_safety_case',
    'verify_scan',
  ];
  return reviewKinds.some((kind) => (
    (evidencePath && (keys.has(`${conditionId}::${kind}::${evidencePath}`) || keys.has(`${conditionId}::${kind}`)))
    || (casePath ? keys.has(`${conditionId}::${kind}::${casePath}`) : false)
  ));
}

export function resolveOverviewV2ZonePrimaryAction(args: {
  attentionOwnsNextAction: boolean;
  attentionRequired?: boolean;
  isCanonical?: boolean;
  evidencePath?: string | null;
  casePath?: string | null;
  zonePath?: string | null;
  createCasePath?: string | null;
}): {
  primaryHref: string | null;
  primaryLabel: string;
  showInvestigate: boolean;
  showCaseAction: boolean;
  caseHref: string;
  caseLabel: string;
} {
  const caseHref = args.casePath || args.createCasePath || '';
  const caseLabel = args.casePath ? 'Open safety case' : 'Create safety case';
  if (args.attentionOwnsNextAction) {
    const primaryHref = args.casePath || args.zonePath || null;
    return {
      primaryHref,
      primaryLabel: args.casePath ? 'Open safety case' : 'Open zone',
      showInvestigate: false,
      showCaseAction: Boolean(caseHref) && primaryHref !== args.casePath,
      caseHref,
      caseLabel,
    };
  }
  const reviewPrimary = Boolean(args.attentionRequired || args.isCanonical);
  const primaryHref = reviewPrimary
    ? (args.evidencePath || null)
    : (args.zonePath || args.evidencePath || null);
  return {
    primaryHref,
    primaryLabel: reviewPrimary ? 'Review evidence' : 'Open zone',
    showInvestigate: Boolean(args.evidencePath && args.evidencePath !== primaryHref),
    showCaseAction: Boolean(caseHref),
    caseHref,
    caseLabel,
  };
}

type OverviewV2AttentionPolicy = {
  conditionId: string;
  label: string;
  location: string;
  attentionRequired?: boolean;
  attentionReason?: string | null;
  coverageIncomplete?: boolean;
  coverageShortfallReason?: string | null;
  coverageLabel?: string | null;
  currentThrough?: string | null;
  cameraId?: string | null;
  measurementValue?: number | null;
  supportingActivityLabel?: string | null;
  currentActivityCount?: number | null;
  evidencePath?: string | null;
  zonePath?: string | null;
  recordPath?: string | null;
  casePath?: string | null;
  scanVerifyPath?: string | null;
  pendingReviewCount?: number | null;
  reviewSetId?: string | null;
  nextAction?: string | null;
};

function coverageIssueDedupeKey(policy: OverviewV2AttentionPolicy): string {
  return [
    'coverage_incomplete',
    text(policy.cameraId) || text(policy.location) || 'site',
    text(policy.currentThrough) || text(policy.coverageLabel) || '',
    text(policy.coverageShortfallReason) || 'coverage_incomplete',
  ].join('|');
}

function operationalNowActivityCount(policy: OverviewV2AttentionPolicy): number | null {
  if (typeof policy.currentActivityCount === 'number' && Number.isFinite(policy.currentActivityCount) && policy.currentActivityCount > 0) {
    return policy.currentActivityCount;
  }
  if (typeof policy.measurementValue === 'number' && Number.isFinite(policy.measurementValue)) {
    return policy.measurementValue;
  }
  const match = text(policy.supportingActivityLabel).match(/^([\d,]+)\b/);
  if (!match) return null;
  const count = Number(match[1].replace(/,/g, ''));
  return Number.isFinite(count) ? count : null;
}

/** Policy NOW or provisional current activity → one attention item per condition, never per occurrence. */
export function buildCurrentConditionAttentionItems(
  policies: OverviewV2AttentionPolicy[],
): OverviewV2AttentionItem[] {
  const seen = new Set<string>();
  const items: OverviewV2AttentionItem[] = [];
  for (const policy of policies) {
    const conditionId = text(policy.conditionId);
    if (!conditionId || seen.has(conditionId)) continue;
    const activity = operationalNowActivityCount(policy);
    if (activity == null || activity <= 0) continue;
    seen.add(conditionId);
    const pendingReviewCount = resolveOverviewV2PendingReviewCount(policy);
    const next = resolveOverviewV2NextAction({
      source: 'current_condition',
      pendingReviewCount,
      reviewSetId: policy.reviewSetId,
      nextAction: policy.nextAction,
      attentionReason: policy.attentionReason,
      casePath: policy.casePath,
      scanVerifyPath: policy.scanVerifyPath,
    });
    const targetPath = next.kind === 'verify_scan'
      ? (policy.scanVerifyPath || policy.casePath || policy.evidencePath || null)
      : next.kind === 'open_safety_case'
        ? (policy.casePath || policy.evidencePath || null)
        : (policy.evidencePath || null);
    const provisional = typeof policy.currentActivityCount === 'number' && policy.currentActivityCount > 0;
    items.push({
      id: `current:${conditionId}`,
      conditionId,
      reason: `${overviewV2ConditionDisplayName(policy.label) || 'Condition'} activity needs review`,
      detail: provisional ? 'Current activity' : 'Last completed measurement',
      location: policy.location,
      targetPath,
      ctaLabel: next.label,
      conditionLabel: policy.label,
      pendingReviewCount,
      nextActionKind: next.kind,
      source: 'current_condition',
      classification: 'DISTINCT_VALID_WORK',
    });
  }
  return items;
}

function buildCoverageAttentionItems(policies: OverviewV2AttentionPolicy[]): OverviewV2AttentionItem[] {
  const groups = new Map<string, OverviewV2AttentionPolicy[]>();
  for (const policy of policies) {
    if (!policy.coverageIncomplete) continue;
    const key = coverageIssueDedupeKey(policy);
    const list = groups.get(key) || [];
    list.push(policy);
    groups.set(key, list);
  }
  return Array.from(groups.entries()).map(([key, group]) => {
    const labels = Array.from(new Set(group.map((row) => overviewV2ConditionDisplayName(row.label) || text(row.label)).filter(Boolean)));
    const shared = group.length > 1;
    const primary = group[0];
    const next = resolveOverviewV2NextAction({ source: 'coverage' });
    const reason = shared
      ? `Coverage incomplete — ${text(primary.location) || 'this camera'}`
      : `Coverage incomplete — ${overviewV2ConditionDisplayName(primary.label) || text(primary.location) || 'condition'}`;
    return {
      id: `coverage:${key}`,
      conditionId: primary.conditionId,
      reason,
      detail: shared ? `Affects: ${labels.join(', ')}` : 'Measurement quality',
      location: primary.location,
      targetPath: primary.zonePath || primary.evidencePath || null,
      ctaLabel: next.label,
      conditionLabel: primary.label,
      nextActionKind: next.kind,
      source: 'coverage' as const,
      classification: 'DISTINCT_VALID_WORK' as const,
      affectedPolicies: labels,
      dedupeKey: key,
    };
  });
}

export function selectOverviewV2AttentionItems(args: {
  policies: OverviewV2AttentionPolicy[];
  openWork?: MeasurementOpenWorkItem[];
  decisionActions?: Array<{ id?: string | null; objectLabel?: string | null }>;
}): OverviewV2AttentionItem[] {
  const currentCondition = buildCurrentConditionAttentionItems(args.policies);
  const fromPolicies = args.policies
    .filter((policy) => policy.attentionRequired)
    .map((policy) => {
      const pendingReviewCount = resolveOverviewV2PendingReviewCount(policy);
      const copy = customerAttentionCopy({
        label: policy.label,
        location: policy.location,
        attentionReason: policy.attentionReason,
        conditionId: policy.conditionId,
        decisionActions: args.decisionActions,
      });
      const next = resolveOverviewV2NextAction({
        source: 'condition_attention',
        pendingReviewCount,
        reviewSetId: policy.reviewSetId,
        nextAction: policy.nextAction,
        attentionReason: policy.attentionReason,
        casePath: policy.casePath,
        scanVerifyPath: policy.scanVerifyPath,
      });
      const targetPath = next.kind === 'verify_scan'
        ? (policy.scanVerifyPath || policy.casePath || policy.evidencePath || null)
        : next.kind === 'open_safety_case'
          ? (policy.casePath || policy.evidencePath || null)
          : (policy.evidencePath || null);
      return {
        id: `condition:${policy.conditionId}`,
        conditionId: policy.conditionId,
        reason: copy.reason,
        detail: copy.detail,
        location: policy.location,
        targetPath,
        ctaLabel: next.label,
        conditionLabel: policy.label,
        pendingReviewCount,
        nextActionKind: next.kind,
        source: 'condition_attention' as const,
        classification: 'DISTINCT_VALID_WORK' as const,
      };
    });
  const fromCoverage = buildCoverageAttentionItems(args.policies);
  const fromOpenWork = (args.openWork || [])
    .filter((item) => HUMAN_OPEN_WORK.has(item.kind))
    .map((item) => {
      const policy = args.policies.find((row) => row.conditionId === item.conditionId);
      const pendingReviewCount = resolveOverviewV2PendingReviewCount(policy);
      const next = resolveOverviewV2NextAction({
        source: 'open_work',
        pendingReviewCount,
        reviewSetId: policy?.reviewSetId,
        nextAction: policy?.nextAction,
        openWorkKind: item.kind,
        casePath: policy?.casePath,
        scanVerifyPath: policy?.scanVerifyPath,
      });
      return {
        id: item.id,
        conditionId: item.conditionId,
        reason: item.label,
        detail: 'Work waiting on a person',
        location: undefined,
        targetPath: next.kind === 'open_safety_case'
          ? (policy?.casePath || policy?.evidencePath || null)
          : next.kind === 'verify_scan'
            ? (policy?.scanVerifyPath || policy?.casePath || policy?.evidencePath || null)
            : (policy?.evidencePath || null),
        ctaLabel: next.label,
        conditionLabel: policy?.label,
        pendingReviewCount,
        nextActionKind: next.kind,
        source: 'open_work' as const,
        classification: 'DISTINCT_VALID_WORK' as const,
      };
    });
  return classifyOverviewV2AttentionWork([
    ...currentCondition,
    ...fromPolicies,
    ...fromOpenWork,
    ...fromCoverage,
  ]);
}

export function selectOverviewV2AttentionItem(args: {
  policies: Array<{
    conditionId: string;
    label: string;
    location: string;
    attentionRequired?: boolean;
    attentionReason?: string | null;
    evidencePath?: string | null;
  }>;
  openWork?: MeasurementOpenWorkItem[];
  decisionActions?: Array<{ id?: string | null; objectLabel?: string | null }>;
}): OverviewV2AttentionItem | null {
  return selectOverviewV2AttentionItems(args)[0] || null;
}

export function overviewV2AttentionBannerCopy(item: OverviewV2AttentionItem | null): { title: string; sentence: string } | null {
  if (!item) return null;
  if (item.source === 'current_condition' || item.source === 'coverage') {
    return { title: text(item.reason) || 'Attention needed', sentence: text(item.detail) || 'Work waiting on a person' };
  }
  const pending = finitePendingCount(item.pendingReviewCount);
  const reason = text(item.reason) || text(item.conditionLabel) || 'Condition';
  const title = pending != null
    ? reason.replace(/\s+requires review$/i, '') || reason
    : /requires review/i.test(reason)
      ? reason
      : `${reason} requires review`;
  const sentence = pending != null
    ? `${formatOverviewV2Count(pending)} observation${pending === 1 ? '' : 's'} require review`
    : (text(item.detail) || 'Work waiting on a person');
  return { title, sentence };
}

export function overviewV2SiteAttentionBanner(items: OverviewV2AttentionItem[]): { title: string; sentence: string } {
  const safetyItems = items.filter((item) => item.source !== 'coverage');
  if (!safetyItems.length) {
    return { title: 'No action required', sentence: 'No open work is waiting on a person.' };
  }
  const currentCount = safetyItems.filter((item) => item.source === 'current_condition').length;
  const hasCurrent = currentCount > 0;
  const hasPersonWork = safetyItems.some((item) => item.source === 'open_work');
  const hasCase = safetyItems.some((item) => item.source === 'condition_attention');
  const categoryCount = [hasCurrent, hasPersonWork, hasCase].filter(Boolean).length;
  if (categoryCount > 1) {
    return {
      title: 'Attention needed',
      sentence: hasCurrent
        ? `${formatOverviewV2Count(currentCount)} current condition${currentCount === 1 ? ' has' : 's have'} measured activity.`
        : 'Follow-up is waiting on a person.',
    };
  }
  if (hasCurrent) {
    return {
      title: 'Review current safety activity',
      sentence: currentCount === 1
        ? '1 current condition has measured activity.'
        : `${formatOverviewV2Count(currentCount)} current conditions have measured activity.`,
    };
  }
  const fallback = overviewV2AttentionBannerCopy(safetyItems[0] || null);
  return fallback || { title: 'Follow-up required', sentence: 'Work waiting on a person.' };
}

export function overviewV2AttentionBannerSentence(items: OverviewV2AttentionItem[]): string | null {
  if (!items.length) return null;
  const copy = overviewV2SiteAttentionBanner(items);
  return `${copy.title}. ${copy.sentence}`;
}

export function overviewV2AttentionSummaryDetail(items: OverviewV2AttentionItem[]): string {
  if (!items.length) return 'No person-owned work';
  return items.length === 1 ? 'condition requiring review' : 'conditions requiring review';
}

export function overviewV2ControlFollowUpDetail(args: {
  controlId?: string | null;
  followUpState?: string | null;
  controlName?: string | null;
}): string {
  if (!text(args.controlId)) return 'Not currently under follow-up';
  const followUp = text(args.followUpState).toLowerCase().replace(/_/g, ' ');
  if (followUp.includes('not comparable')) return 'Follow-up not yet comparable';
  return text(args.controlName) || 'Control under follow-up';
}

export function overviewV2PolicyNowIsMeasuring(
  rows?: Array<{
    measurementValue?: number | null;
    supportingActivityLabel?: string | null;
  }> | null,
): boolean {
  return (rows || []).some((row) => (
    row.measurementValue != null
    || Boolean(text(row.supportingActivityLabel))
  ));
}

export function overviewV2OnlineOfflineDetail(onlineCount: number | null, offlineCount: number | null): string {
  if (onlineCount == null || offlineCount == null) return 'Camera status unavailable';
  return `${onlineCount} online · ${offlineCount} offline`;
}

export function buildOverviewV2SummaryTiles(args: {
  zoneCount: number;
  onlineCount: number | null;
  offlineCount: number | null;
  policyCount: number | null;
  patternDecisionCount: number;
  attentionCount: number;
  attentionDetail: string;
  controlFollowUpCount: number;
  controlDetail: string;
  cameraHealthValue: string;
  availabilityDetail: string;
  countPresentation?: 'measured' | 'unavailable' | 'partial' | 'stale';
}): OverviewV2SummaryTile[] {
  const presentation = args.countPresentation || 'measured';
  const tiles: OverviewV2SummaryTile[] = [
    {
      id: 'zones',
      label: 'Monitored areas',
      value: formatOverviewV2Count(args.zoneCount),
      detail: overviewV2OnlineOfflineDetail(args.onlineCount, args.offlineCount),
    },
    {
      id: 'policies',
      label: 'Safety conditions',
      value: args.policyCount == null ? 'Unavailable' : formatOverviewV2Count(args.policyCount),
      detail: `Across ${formatOverviewV2Count(args.zoneCount)} ${args.zoneCount === 1 ? 'area' : 'areas'}`,
    },
    {
      id: 'patterns',
      label: 'Reviews pending',
      value: formatOverviewV2Count(args.patternDecisionCount),
      detail: args.patternDecisionCount === 0
        ? 'No recurring reviews awaiting decision'
        : 'Recurring reviews awaiting decision',
    },
    {
      id: 'attention',
      label: 'Current activity',
      value: formatOverviewV2Count(args.attentionCount),
      detail: args.attentionDetail,
      tone: 'default',
    },
    {
      id: 'controls',
      label: 'Controls needing follow-up',
      value: formatOverviewV2Count(args.controlFollowUpCount),
      detail: args.controlDetail,
    },
    {
      id: 'health',
      label: 'Cameras available',
      value: args.cameraHealthValue,
      detail: text(args.availabilityDetail) || 'Current availability',
    },
  ];
  if (presentation === 'unavailable' || presentation === 'partial') {
    const value = presentation === 'partial' ? 'Partial' : 'Unavailable';
    const detail = presentation === 'partial'
      ? 'Site-wide total withheld because a condition read failed'
      : 'Required data could not be retrieved';
    return tiles.map((tile) => ({ ...tile, value, detail, tone: 'default' as const }));
  }
  if (presentation === 'stale') {
    return tiles.map((tile) => ({
      ...tile,
      detail: `STALE · ${tile.detail}`,
    }));
  }
  return tiles;
}
